/**
 * The public pages a visitor's trail records before the family gets in touch,
 * so a lead can say what the family read: a Chemistry page means Chemistry.
 *
 * The browser keeps the trail and the server accepts only the paths this
 * module recognises, so both agree on what a page is. Pages are named on the
 * server (`server/services/leadPages.ts`), which can read the school catalog
 * this module must keep out of the client bundle.
 */
import { isMathCheckPath } from './mathCheck.js';
import { AREA_ROUTE_PATHS } from './schools/schoolAreas.js';
import {
  isPublicMarketingRoutePath,
  normalizePublicMarketingRoutePath,
} from './seo/publicMarketingRoutes.js';

/** The most pages a trail keeps: the latest ones, oldest dropped first. */
export const LEAD_PAGE_TRAIL_LIMIT = 15;

/** One page a visitor viewed, as the browser sends it. */
export interface LeadPageVisit {
  path: string;
  /** When the visitor first opened the page (ISO 8601). */
  at: string;
}

/** Public pages that are not in the sitemap but still say what a family wants. */
const UNLISTED_PAGE_PATHS: ReadonlySet<string> = new Set([
  '/demo/practice',
  '/psle-math-practice',
  '/register',
  ...AREA_ROUTE_PATHS,
]);

/** Public pages that say nothing about what a family is looking for. */
const SKIPPED_PAGE_PATHS: ReadonlySet<string> = new Set(['/terms', '/privacy']);

/**
 * The canonical path of a public page a lead's trail records ("/chemistry"),
 * from a path or a full URL, or null for app, staff and legal pages. Every
 * math check URL is the one page "/math-check", so no partner link or later
 * step's address is kept.
 */
export function leadPagePath(pathOrUrl: unknown): string | null {
  if (typeof pathOrUrl !== 'string') return null;
  let raw = pathOrUrl.trim();
  if (/^https?:\/\//i.test(raw)) {
    try {
      raw = new URL(raw).pathname;
    } catch {
      return null;
    }
  }
  // React Router matches paths case-insensitively, and every public route
  // is lowercase.
  const path = normalizePublicMarketingRoutePath(raw.toLowerCase());
  if (!path || SKIPPED_PAGE_PATHS.has(path)) return null;
  if (isMathCheckPath(path)) return '/math-check';
  return isPublicMarketingRoutePath(path) || UNLISTED_PAGE_PATHS.has(path)
    ? path
    : null;
}

/** "Secondary Chemistry → Sec 3 Pure Chemistry": named pages, in visit order. */
export function formatLeadPages(names: readonly string[]): string {
  return names.join(' → ');
}
